import { DatasheetElement, DatasheetElementId } from "@modules/datasheets/types/datasheet-element";
import { DatasheetElementAlignment, DatasheetElementSizeAlignment } from "../../types/alignment";

type Props = {
  datasheetElements: Record<DatasheetElementId, DatasheetElement>;
  getDatasheetElement: (id: DatasheetElementId) => DatasheetElement | undefined;
  selectedDatasheetElementsId: Array<DatasheetElementId>;
  onDatasheetElementsChange: (datasheetElements: Record<DatasheetElementId, DatasheetElement>) => void;
};

export const useDatasheetElementsAlignmentController = (props: Props) => {
  const { getDatasheetElement, selectedDatasheetElementsId, onDatasheetElementsChange, datasheetElements } = props;

  const handleDatasheetElementsAlignmentChange = (alignment: DatasheetElementAlignment) => {
    let getUpdatedCoordinates: (dEl: DatasheetElement) => { x?: number; y?: number } | undefined;

    const getSortedDatasheetElementsBy = (sortFn: (a: DatasheetElement, b: DatasheetElement) => number) => {
      return selectedDatasheetElementsId.map(x => getDatasheetElement(x)).sort((element1, element2) => {
        if (!element1 || !element2) {
          return 0;
        }

        return sortFn(element1, element2);
      });
    };

    if ([DatasheetElementAlignment.left, DatasheetElementAlignment.right].includes(alignment)) {
      const sortedHorizontallyByPosition = getSortedDatasheetElementsBy((a, b) => a.coordinates.x - b.coordinates.x);

      const coordinatesConfig: Partial<Record<DatasheetElementAlignment, DatasheetElement>> = {
        [DatasheetElementAlignment.left]: sortedHorizontallyByPosition[0]!,
        [DatasheetElementAlignment.right]: sortedHorizontallyByPosition[sortedHorizontallyByPosition.length - 1]!,
      };
      
      const referenceElement = coordinatesConfig[alignment]!;
      const baseX = referenceElement.coordinates.x;

      getUpdatedCoordinates = (dEl) => {
        const x = baseX + (alignment === DatasheetElementAlignment.left ? 0 : referenceElement.width - dEl.width);

        return { x };
      };
    }

    if ([DatasheetElementAlignment.top, DatasheetElementAlignment.bottom].includes(alignment)) {
      const sortedVerticallyByPosition = getSortedDatasheetElementsBy((a, b) => a.coordinates.y - b.coordinates.y);

      const coordinatesConfig: Partial<Record<DatasheetElementAlignment, DatasheetElement>> = {
        [DatasheetElementAlignment.top]: sortedVerticallyByPosition[0]!,
        [DatasheetElementAlignment.bottom]: sortedVerticallyByPosition[sortedVerticallyByPosition.length - 1]!,
      };

      const referenceElement = coordinatesConfig[alignment]!;
      const baseY = referenceElement.coordinates.y;

      getUpdatedCoordinates = (dEl) => {
        const y = baseY + (alignment === DatasheetElementAlignment.top ? 0 : referenceElement.height - dEl.height);

        return { y };
      };
    }

    const updatedElements = selectedDatasheetElementsId.reduce((acc, id) => {
      const element = getDatasheetElement(id);

      if (!element) {
        return acc;
      }

      acc[id] = {
        ...element,
        coordinates: {
          ...element.coordinates,
          ...getUpdatedCoordinates(element),
        },
      };

      return acc;
    }, { ...datasheetElements });

    onDatasheetElementsChange(updatedElements);
  };

  const handleDatasheetElementsSizeChange = (sizeAlignment: DatasheetElementSizeAlignment) => {
    const firstSelectedElement = selectedDatasheetElementsId.length
      ? getDatasheetElement(selectedDatasheetElementsId[0]!)
      : undefined;

    if (!firstSelectedElement) {
      return;
    }

    const updatedElements = selectedDatasheetElementsId.reduce((acc, id) => {
      const element = getDatasheetElement(id);

      if (!element) {
        return acc;
      }

      const sizeConfig: Record<DatasheetElementSizeAlignment, { width?: number; height?: number }> = {
        [DatasheetElementSizeAlignment.horizontal]: {
          width: firstSelectedElement.width,
        },
        [DatasheetElementSizeAlignment.vertical]: {
          height: firstSelectedElement.height,
        },
      };

      acc[id] = {
        ...element,
        ...sizeConfig[sizeAlignment],
      };

      return acc;
    }, { ...datasheetElements });

    onDatasheetElementsChange(updatedElements);
  };

  return {
    handleDatasheetElementsAlignmentChange,
    handleDatasheetElementsSizeChange,
  };
};